import { Alert, AlertDescription, AlertTitle, Button, CardContent } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { RecordSection, StatusChip } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  IAdminDisclosure,
  IAdminDisclosureFirm,
  IAdminDisclosureGroup,
  IAdminDisclosureSubject,
  IAdminDisclosureSummary,
  IAdminDisclosureU4,
  IAdminParticipantDetail,
  IAdminParticipantHub
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent } from '../../components/account-facts';
import { LoadError, Section, TableSkeleton, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import {
  deeperLevel,
  DisclosureCard,
  DisclosureHeading,
  instantDay,
  type DisclosureLevel
} from '../../components/disclosure-card';
import { openFolds } from '../../components/section-nav';
import { checkedByWords, listedByWords } from '../../disclosure-fields';
import { DisclosureSummary, disclosureSummaryOf } from './disclosure-summary';
import { MemberPart } from './household';
import { HUB_MEMORY_PAGE } from './hub-memory';

/**
 * Every disclosure of each member's CRD set (SR-216's R4) — survey-api's
 * `GET …/participants/{id}/disclosures`, one group per CRD, each disclosure with its non-empty
 * sections, the Salesforce copies it was merged from and its history. The stored records, apart
 * from Registry answers' BrokerCheck reading: that one is FINRA's flags and count in one answer,
 * this is what survey-api keeps, and neither is computed from the other.
 *
 * **Drawn while any member has a CRD set** — a non-null summary on the hub (the plan's Q2, ruled
 * (a)) — which the page decides from the hub document before any read, as it does Team's. Each
 * member's part is headed by its summary (`DisclosureSummary`, the rendering the header and
 * Overview draw); a member whose summary is null has an empty set by survey-api's own statement,
 * so its part is the summary alone and nothing is read for it.
 *
 * **Its own read per member, below a loaded hub**, with the activity's three arms: a skeleton, a
 * load error that never claims there are none, and over a list already drawn, "showing them as
 * last read". A 404 there is a failure said — the household moved between two reads — never an
 * empty list. One read per member, because the route is per participant (survey-api's B2).
 *
 * **It re-reads when that member's summary changes, and only then** — keyed on its `checked`,
 * `refreshedAt`, `liveCount` and `totalCount`. A registry's answer arrives through the hub's poll,
 * and the summary is the hub's own statement that the set moved: a list read once would
 * contradict the line above it, and one re-read on every tick would multiply the poll by the
 * household. Not on `written` either: no write on this page changes a CRD set.
 *
 * **It renders what survey-api computed** (R2): the groups and their order, the order inside a
 * group, which sections are non-empty, what changed. Nothing here sorts, canonicalises a CRD,
 * claims which group is the member's own — the wire carries no `own`, and a CRD the ETL parked
 * under a person's record reads exactly like theirs — or counts a list: the counts are the
 * summary's and the subject's, drawn as sent even where one differs from the list. The words
 * are `disclosure-fields.ts`'.
 *
 * **What it drew is the Salesforce tasks' way to a disclosure** (`useDrawnDisclosures`): a task
 * that names one is drawn by the disclosure's type and day and scrolls to it, so each member's
 * part reports its groups as drawn, rather than the tasks section reading the route again. New
 * task (`work-items.tsx`) offers a member's drawn disclosures from the same place, and a work
 * item's pane leads here with the disclosure to open in its history state (`IRevealState`), which
 * the page honours once that member's part has drawn it.
 *
 * **Each disclosure is `DisclosureCard`** (`components/disclosure-card.tsx`), the rendering a
 * disclosure work item's pane draws too; this section keeps the groups, the switch and the reveal.
 *
 * **"Show" (CMP-14, SR-222's F9 R12) filters what is drawn, and computes nothing.** A switch per
 * member's part over `live` as survey-api sends it — every disclosure, those a registry lists
 * (BrokerCheck or, since SR-396, IAPD), those no registry lists any more (the owner's ruling on
 * F9's Q3: `live` names what survey-api knows, and covers an expunged one however it left; the
 * words SR-396's Q1). It hides rows of the list already read and nothing else: the
 * group headings keep survey-api's counts, a group left with nothing shown says so, and the choice
 * is the viewer's for this visit, not stored — the Team firms precedent of a filter over a result.
 * A hidden row stays in the document, so a press that leads to it (a task, a work item) finds it,
 * and switching its part back to every disclosure is part of the reveal.
 */
export function DisclosuresSection({
  hub,
  members,
  anchor,
  onDrawn,
  nameOf
}: {
  hub: IAdminParticipantHub;
  members: IAdminParticipantDetail[];
  anchor: string;
  onDrawn: ReportDrawn;
  nameOf: CrdName;
}) {
  const multi = members.length > 1;
  return (
    <Section
      title="Disclosures"
      anchor={anchor}
      description={
        'What the system holds for each CRD: a BrokerCheck or IAPD lookup that finds the person ' +
        'updates these, while a miss or an error changes nothing, and migrated ones are ' +
        'Salesforce’s as of the migration. FINRA’s own flags and count, from one answer, are ' +
        'under Registry answers.'
      }
    >
      <CardContent className="grid gap-8">
        {members.map((member, index) => {
          const summary = disclosureSummaryOf(hub, member.participantId);
          return (
            <MemberPart
              key={member.participantId}
              member={member}
              opened={index === 0}
              multi={multi}
            >
              {summary !== undefined && (
                <p>
                  <DisclosureSummary summary={summary} />
                </p>
              )}
              {summary && (
                <MemberDisclosures
                  participantId={member.participantId}
                  summary={summary}
                  level={multi ? 4 : 3}
                  onDrawn={onDrawn}
                  nameOf={nameOf}
                />
              )}
            </MemberPart>
          );
        })}
      </CardContent>
    </Section>
  );
}

/** One member's part as drawn: its groups as last read, or `'failed'` — a first read that failed,
 *  so nothing is. */
type Drawn = IAdminDisclosureGroup[] | 'failed';

/** What a member's part says up as it draws; `undefined` as it leaves the page. */
export type ReportDrawn = (participantId: string, drawn: Drawn | undefined) => void;

/**
 * Where a disclosure a Salesforce task names stands on this page: drawn, under a member's group
 * (`participantId` the member whose part draws it); unknown, while a member's part that could
 * hold it is still on its first read or its read failed; or not on this page at all — its source
 * filed under another record, or no Disclosures section drawn. Only the last is said as such:
 * while a read is out, or after one failed, the page cannot know (gate 2's ruling, 2026-10-01).
 */
export type DisclosureLookup =
  | { state: 'drawn'; disclosure: IAdminDisclosure; participantId: string }
  | { state: 'unknown' }
  | { state: 'absent' };

/** Finds a disclosure a task names, preferring the copy in the task's own member's part. */
export type FindDisclosure = (disclosureId: string, participantId: string) => DisclosureLookup;

/**
 * The disclosures the section has drawn, held by the page for the Salesforce tasks — `onDrawn`
 * for the section, `find` for the tasks. Which members are still reading is the hub document's
 * statement: a member with a non-null summary is read, one with a null summary never is.
 *
 * **One disclosure can be drawn twice.** A team's CRD set takes in its members' CRDs, so a
 * household of a person and their team draws the person's disclosures in both parts. `find`
 * looks in the task's own member's part first and then in the others in the page's order, and
 * each copy is its own anchor (`disclosureAnchor`), so a task leads to the copy under its member.
 */
export function useDrawnDisclosures(hub: IAdminParticipantHub): {
  onDrawn: ReportDrawn;
  find: FindDisclosure;
  /** A member's disclosures as its part drew them, in its order — `undefined` while its part is
   *  on its first read, failed, or not drawn at all. */
  disclosuresOf: (participantId: string) => IAdminDisclosure[] | undefined;
  /** A member's part as drawn — its groups, or `'failed'` — for a survey tab's copy (SR-426);
   *  `undefined` while its part is on its first read or not drawn. */
  groupsOf: (participantId: string) => Drawn | undefined;
} {
  const [drawn, setDrawn] = React.useState<ReadonlyMap<string, Drawn>>(() => new Map());

  // A disclosure the page was opened to show — a work item's pane leads here with it — opened
  // once its member's part has drawn it, and once only: a later read draws it again, and must not
  // pull the page back to it.
  const { state } = useLocation();
  const arrival = React.useRef(revealOf(state));
  React.useEffect(() => {
    if (arrival.current !== null && revealDisclosureAt(arrival.current)) arrival.current = null;
  }, [drawn]);

  const onDrawn = React.useCallback<ReportDrawn>((participantId, next) => {
    setDrawn((current) => {
      if (current.get(participantId) === next) return current;
      const copy = new Map(current);
      if (next === undefined) copy.delete(participantId);
      else copy.set(participantId, next);
      return copy;
    });
  }, []);

  const find: FindDisclosure = (disclosureId, participantId) => {
    const members = hub.disclosures.map((entry) => entry.participantId);
    const order = [participantId, ...members.filter((member) => member !== participantId)];
    for (const member of order) {
      const part = drawn.get(member);
      if (part === undefined || part === 'failed') continue;
      for (const group of part) {
        const disclosure = group.disclosures.find((candidate) => candidate.id === disclosureId);
        if (disclosure) return { state: 'drawn', disclosure, participantId: member };
      }
    }
    // A part still on its first read, or one whose read failed, could hold it.
    const unknown = hub.disclosures.some(
      (entry) =>
        entry.summary !== null &&
        (!drawn.has(entry.participantId) || drawn.get(entry.participantId) === 'failed')
    );
    return unknown ? { state: 'unknown' } : { state: 'absent' };
  };
  const disclosuresOf = (participantId: string) => {
    const part = drawn.get(participantId);
    if (part === undefined || part === 'failed') return undefined;
    return part.flatMap((group) => group.disclosures);
  };
  const groupsOf = (participantId: string) => drawn.get(participantId);
  return { onDrawn, find, disclosuresOf, groupsOf };
}

/** What a link to the hub carries to open one disclosure on arrival: `disclosureAnchor`'s id. */
export interface IRevealState {
  reveal: string;
}

/** The anchor a history entry asks the page to open, or null — the state is whatever the entry
 *  carries, so only a string is taken. */
function revealOf(state: unknown): Nullable<string> {
  const reveal = (state as Partial<IRevealState> | null)?.reveal;
  return typeof reveal === 'string' && reveal !== '' ? reveal : null;
}

/** The anchor of one drawn copy of a disclosure — per member, since a household can draw one
 *  disclosure in two members' parts, and an id has to name one element. */
export function disclosureAnchor(participantId: string, disclosureId: string): string {
  return `disclosure-${participantId}-${disclosureId}`;
}

function MemberDisclosures({
  participantId,
  summary,
  level,
  onDrawn,
  nameOf
}: {
  participantId: string;
  summary: IAdminDisclosureSummary;
  level: DisclosureLevel;
  onDrawn: ReportDrawn;
  nameOf: CrdName;
}) {
  // The summary's four members, as one dependency: a new hub document on every poll tick is a
  // new object, and only a moved set is a reason to read again.
  const moved = [summary.checked, summary.refreshedAt, summary.liveCount, summary.totalCount].join(
    ' '
  );
  const { data, error } = useAdminData(
    (signal) => participantsApi.listDisclosures(participantId, signal),
    [participantId, moved]
  );

  // What is on screen, said up as it changes: the groups as last read, or — a first read that
  // failed — nothing drawn. A part that leaves the page takes its groups with it.
  const failed = data === null && error !== null;
  React.useEffect(() => {
    if (data !== null) onDrawn(participantId, data);
    else if (failed) onDrawn(participantId, 'failed');
    return () => onDrawn(participantId, undefined);
  }, [participantId, data, failed, onDrawn]);

  return (
    <div className="grid gap-4">
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh the disclosures — showing them as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={2} />
      ) : (
        <DisclosureGroups
          participantId={participantId}
          groups={data}
          level={level}
          nameOf={nameOf}
          anchored
        />
      )}
    </div>
  );
}

/**
 * A member's CRD groups as read, with CMP-14's switch over them — the Disclosures section's part
 * and a survey tab's copy of it (SR-426) alike, each switch the viewer's for its own copy. Only the
 * section's copy is `anchored`: a reveal finds one element per disclosure, and the record tab's
 * stays mounted under a survey tab, so a second copy carrying the same ids would be two.
 */
function DisclosureGroups({
  participantId,
  groups,
  level,
  nameOf,
  anchored
}: {
  participantId: string;
  groups: IAdminDisclosureGroup[];
  level: DisclosureLevel;
  nameOf: CrdName;
  anchored: boolean;
}) {
  // CMP-14's switch: the viewer's, for this visit. A reveal of a row it hides asks for every one,
  // synchronously, so the row is on screen before the reveal scrolls to it.
  const [view, setView] = React.useState<DisclosureView>('all');
  const part = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const element = part.current;
    if (!element || !anchored) return;
    const showEvery = () => flushSync(() => setView('all'));
    element.addEventListener(SHOW_EVERY, showEvery);
    return () => element.removeEventListener(SHOW_EVERY, showEvery);
  }, [anchored]);
  const any = groups.some((group) => group.disclosures.length > 0);

  return (
    <div ref={part} className="grid gap-4">
      {any && <ViewSwitch view={view} onView={setView} />}
      {groups.length === 0 ? (
        // The summary said the set was not empty and this read says it is: the set moved between
        // the two reads, and the hub read again — a write's re-read, a poll tick or a reload —
        // says so. Nothing is read for it here: the page reads the hub on its own terms.
        <p className="text-sm">{absent('No CRD came back with this read.')}</p>
      ) : (
        <div className="grid gap-6">
          {groups.map((group) => (
            <DisclosureGroup
              key={group.crd}
              participantId={participantId}
              group={group}
              name={group.advisorName ?? nameOf(participantId, group.crd)}
              level={level}
              view={view}
              anchored={anchored}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** The advisor a CRD names, for a group survey-api sent no `advisorName` for (SR-426) — a person's
 *  own name on their own CRD, or a team's or firm's roster slot holding it — or null where the page
 *  knows none. */
export type CrdName = (participantId: string, crd: string) => Nullable<string>;

/**
 * A member's disclosures on a survey tab (SR-426): the Disclosures section's groups, drawn again
 * inside the survey's Veritant research part, so a reader of the survey's U4 sees what each
 * disclosure entails without leaving the tab. **Nothing is read here**: the record tab's section,
 * mounted under every tab, read them, and its part hands them up (`useDrawnDisclosures`' `groupsOf`)
 * — a second read would double what the page asks of survey-api per member. A fold of its own,
 * open at first like U4, its cards folded; drawn, as the section is, only for a member with a CRD
 * set.
 */
export function SurveyTabDisclosures({
  participantId,
  surveyCode,
  summary,
  drawn,
  nameOf,
  anchor
}: {
  participantId: string;
  surveyCode: string;
  /** A section of the survey tab under this anchor (SR-474), a level-2 fold named in its nav;
   *  absent, a fold inside the Veritant research part. */
  anchor?: string;
  /** The member's hub summary — null for no CRD set, `undefined` where the hub carries none. */
  summary: IAdminDisclosureSummary | null | undefined;
  drawn: Drawn | undefined;
  nameOf: CrdName;
}) {
  // The Disclosures section's rule: a member with no CRD set has nothing to draw.
  if (!summary) return null;
  return (
    <RecordSection
      page={HUB_MEMORY_PAGE}
      sectionKey={`research.${surveyCode}.disclosures`}
      title="Disclosures"
      level={anchor ? 2 : 4}
      anchor={anchor}
      defaultOpen
      // It counts no fields: the summary under its title is survey-api's count.
      filled={0}
      total={0}
    >
      <div data-survey-disclosures={participantId} className="grid gap-4 text-sm">
        <p>
          <DisclosureSummary summary={summary} />
        </p>
        {drawn === undefined ? (
          <p>{absent('Reading the disclosures…')}</p>
        ) : drawn === 'failed' ? (
          <p>
            {absent(
              'The disclosures could not be read — the record’s Disclosures section says why.'
            )}
          </p>
        ) : (
          <DisclosureGroups
            participantId={participantId}
            groups={drawn}
            level={anchor ? 3 : 5}
            nameOf={nameOf}
            anchored={false}
          />
        )}
      </div>
    </RecordSection>
  );
}

/** Which of a member's disclosures are shown — CMP-14's switch, over `live` as sent: listed by a
 *  registry, BrokerCheck or IAPD (survey-api keeps `live` true exactly while `listedBy` names one),
 *  or by none any more. The view's codes are the hook's spelling and stay as they were. */
type DisclosureView = 'all' | 'live' | 'gone';

const VIEW_LABEL: Record<DisclosureView, string> = {
  all: 'All',
  live: 'Listed',
  gone: 'No longer listed'
};

const VIEWS = Object.keys(VIEW_LABEL) as DisclosureView[];

function shown(disclosure: IAdminDisclosure, view: DisclosureView): boolean {
  return view === 'all' || disclosure.live === (view === 'live');
}

/** The event a reveal dispatches from a row the switch hides, for its part to show every one. */
const SHOW_EVERY = 'veritant:show-every-disclosure';

/** CMP-14's switch: three presses, the one in force pressed. `data-disclosure-view` is the hook,
 *  holding the view's own spelling. */
function ViewSwitch({
  view,
  onView
}: {
  view: DisclosureView;
  onView: (view: DisclosureView) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Show disclosures"
      data-disclosure-view={view}
      className="flex flex-wrap items-center gap-1.5 text-sm"
    >
      <span className="mr-1 text-muted-foreground">Show</span>
      {VIEWS.map((option) => (
        <Button
          key={option}
          type="button"
          size="sm"
          variant={option === view ? 'default' : 'outline'}
          aria-pressed={option === view}
          onClick={() => onView(option)}
        >
          {VIEW_LABEL[option]}
        </Button>
      ))}
    </div>
  );
}

/** One CRD of the set: its number and, where Salesforce named one, the advisor's name; its last
 *  check; and its disclosures in survey-api's order — those CMP-14's switch hides kept in the
 *  document, hidden. */
function DisclosureGroup({
  participantId,
  group,
  name,
  level,
  view,
  anchored
}: {
  participantId: string;
  group: IAdminDisclosureGroup;
  /** The advisor's name — survey-api's `advisorName`, else `CrdName`'s — or null. */
  name: Nullable<string>;
  level: DisclosureLevel;
  view: DisclosureView;
  anchored: boolean;
}) {
  const id = React.useId();
  const noneShown =
    group.disclosures.length > 0 && !group.disclosures.some((item) => shown(item, view));
  return (
    <div
      data-crd={group.crd}
      role="group"
      aria-labelledby={id}
      // Each CRD a block of its own, its advisor's name the divider between them (SR-426).
      className="grid gap-3 border-t border-border pt-4 first:border-t-0 first:pt-0"
    >
      <DisclosureHeading
        level={level}
        id={id}
        className="flex flex-wrap items-baseline gap-x-2 text-[var(--color-royal-blue)]"
      >
        {name !== null && (
          <>
            <span className="text-base font-semibold">{name}</span>
            <span aria-hidden className="text-muted-foreground">
              ·
            </span>
          </>
        )}
        <span
          className={
            name === null ? 'text-base font-semibold' : 'text-sm font-normal text-muted-foreground'
          }
        >
          CRD {group.crd}
        </span>
      </DisclosureHeading>
      <p data-subject className="text-sm">
        <SubjectLine subject={group.subject} />
      </p>
      {group.u4 && <U4Head crd={group.crd} u4={group.u4} />}
      {group.disclosures.length > 0 ? (
        <>
          <ul aria-label={`CRD ${group.crd}: disclosures`} className="grid gap-2">
            {group.disclosures.map((disclosure) => (
              <DisclosureCard
                key={disclosure.id}
                as="li"
                id={anchored ? disclosureAnchor(participantId, disclosure.id) : undefined}
                disclosure={disclosure}
                level={deeperLevel(level)}
                hidden={!shown(disclosure, view)}
              />
            ))}
          </ul>
          {noneShown && (
            <p data-none-shown className="text-sm">
              {absent('None shown here — switch to All.')}
            </p>
          )}
        </>
      ) : (
        group.subject !== null && (
          <p className="text-sm">{absent('No disclosure is on file for this CRD.')}</p>
        )
      )}
    </div>
  );
}

/** The CRD's last check — who stamped it in `disclosure-fields.ts`' words, an origin it has no
 *  words for by its spelling. */
function SubjectLine({ subject }: { subject: Nullable<IAdminDisclosureSubject> }) {
  if (subject === null) return absent('Never checked');
  return (
    <>
      Checked {formatDateTime(subject.refreshedAt)} by {checkedByWords(subject.refreshedBy)} ·{' '}
      {subject.liveCount} live then
    </>
  );
}

/**
 * The CRD's U4 at a glance (the F2 plan's R12: S3) — what survey-api's staff fields §7 computed,
 * drawn as sent: the live disclosures counted by resolution in its order, how many are new since
 * the participant's last closed cycle, the last refresh — who stamped it in `disclosure-fields.ts`'
 * words — and, on the CRD the latest registry answer was about (BrokerCheck's or, since SR-396,
 * IAPD's), the name, firms and error that answer gave. Nothing is counted here. The names and
 * firms are drawn only when sent; the first three always, their emptiness said ("None live", "No
 * closed cycle", "Never refreshed"); a `lastError` of `""` is an answer without an error ("No
 * error"), null no answer about this CRD at all, drawn as nothing. A group read without `u4` (an
 * API from before it) draws no head — the caller's rule — never an empty one.
 *
 * **"Current firms" replaces the two strings where `firms` is sent** (SR-396's D9, the owner's
 * Q1): one line per firm of the pair, its name, its id and a chip per registry listing it, in
 * survey-api's order; `[]` is an answer that lists no firm. Only a head read without `firms` — an
 * API before SR-396 — or with it null draws "Firm names" and "Firm ids" as before.
 */
function U4Head({ crd, u4 }: { crd: string; u4: IAdminDisclosureU4 }) {
  const facts: [string, React.ReactNode][] = [
    [
      'Live by resolution',
      u4.liveByResolution.length === 0 ? (
        absent('None live')
      ) : (
        <ul className="grid gap-0.5">
          {u4.liveByResolution.map((row, index) => (
            <li key={`${row.resolution} ${index}`}>
              {row.resolution ?? absent('No resolution')}: {row.count}
            </li>
          ))}
        </ul>
      )
    ],
    [
      'New since the last closed cycle',
      u4.newSinceLastClosedCycle === null
        ? absent('No closed cycle')
        : String(u4.newSinceLastClosedCycle)
    ],
    [
      'Refreshed',
      u4.refreshedAt === null
        ? absent('Never refreshed')
        : `${instantDay(u4.refreshedAt)}${
            u4.refreshedBy === null ? '' : ` by ${checkedByWords(u4.refreshedBy)}`
          }`
    ]
  ];
  if (u4.fullName !== null) facts.push(['Full name', u4.fullName]);
  if (u4.firms !== undefined && u4.firms !== null) {
    facts.push(['Current firms', <CurrentFirms crd={crd} firms={u4.firms} />]);
  } else {
    if (u4.firmNames !== null) facts.push(['Firm names', u4.firmNames]);
    if (u4.firmIds !== null) facts.push(['Firm ids', u4.firmIds]);
  }
  if (u4.lastError !== null) {
    facts.push(['Last error', u4.lastError === '' ? absent('No error') : u4.lastError]);
  }
  return (
    <dl
      data-u4={crd}
      aria-label={`CRD ${crd}: U4`}
      className="grid gap-x-6 gap-y-2 rounded-md border border-border p-3 text-sm sm:grid-cols-2"
    >
      {facts.map(([term, value]) => (
        <div key={term} className="grid content-start gap-0.5">
          <dt className="text-xs text-muted-foreground">{term}</dt>
          <dd className="whitespace-pre-line wrap-anywhere">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The pair's current firms, one line each — name, id, and a chip per registry whose answer
 *  lists it. `[data-firm]` holds the id as sent, empty where it is null. */
function CurrentFirms({ crd, firms }: { crd: string; firms: IAdminDisclosureFirm[] }) {
  if (firms.length === 0) return absent('No current firm');
  return (
    <ul aria-label={`CRD ${crd}: current firms`} className="grid gap-1">
      {firms.map((firm, index) => (
        <li
          key={`${firm.firmId} ${index}`}
          data-firm={firm.firmId ?? ''}
          className="flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          <span>{firm.firmName ?? absent('No name given')}</span>
          {firm.firmId !== null && (
            <>
              <span aria-hidden>·</span>
              <span>{firm.firmId}</span>
            </>
          )}
          {firm.listedBy.map((registry) => (
            <StatusChip key={registry} tone="neutral">
              {listedByWords(registry)}
            </StatusChip>
          ))}
        </li>
      ))}
    </ul>
  );
}

/**
 * A disclosure drawn on this page, opened and scrolled to, with the focus on its row — a
 * Salesforce task's way to the disclosure it names, the copy under `participantId` (`find`'s
 * answer). It scrolls and never navigates, the section nav's rule: a fragment would be a history
 * entry without state, and took the way back away.
 */
export function revealDisclosure(participantId: string, disclosureId: string): void {
  revealDisclosureAt(disclosureAnchor(participantId, disclosureId));
}

/** `revealDisclosure` by the row's anchor — whether the row was on the page to reveal. A row
 *  CMP-14's switch hides has its part show every disclosure first. */
function revealDisclosureAt(anchor: string): boolean {
  const item = document.getElementById(anchor);
  if (!item) return false;
  if (item.hidden) item.dispatchEvent(new Event(SHOW_EVERY, { bubbles: true }));
  // The section may be folded (F11's Q2): opened for the visit, as the nav opens one.
  openFolds(item);
  const fold = item.querySelector('details');
  if (fold) fold.open = true;
  item.scrollIntoView({ block: 'start' });
  item.querySelector('summary')?.focus({ preventScroll: true });
  return true;
}

/** "Disclosure: {type}, {event date}" — a disclosure named in the words its row says them in,
 *  by the Salesforce tasks that name one and by New task's choice of one. */
export function disclosureWords({ type, eventDate }: IAdminDisclosure): string {
  const day = eventDate === null ? 'undated' : formatDay(eventDate);
  return `Disclosure: ${type ?? 'no type given'}, ${day}`;
}
